/**
 * Auth Channel Service
 * Cross-tab authentication synchronization using BroadcastChannel API
 * 
 * Features:
 * - Sync login/logout across browser tabs
 * - Broadcast token updates to all tabs
 * - Handle tab-specific session restoration
 * 
 * @author Auth System
 * @version 1.0.0
 */

// Channel name for auth synchronization
const AUTH_CHANNEL_NAME = 'kuppion-auth-sync';

// Message types for cross-tab communication
export const AUTH_EVENTS = {
  LOGIN: 'AUTH_LOGIN',
  LOGOUT: 'AUTH_LOGOUT',
  TOKEN_UPDATE: 'AUTH_TOKEN_UPDATE',
  SESSION_REQUEST: 'AUTH_SESSION_REQUEST',
  SESSION_RESPONSE: 'AUTH_SESSION_RESPONSE',
};

// Singleton channel instance
let authChannel = null;
let messageHandlers = [];

// Throttle mechanism for logout events to prevent loops
let lastLogoutProcessedTime = 0;
let lastLogoutSourceTabId = null;
const LOGOUT_THROTTLE_MS = 10000; // Ignore logout events from same source within 10 seconds
const GLOBAL_LOGOUT_COOLDOWN_MS = 3000; // Global cooldown for any logout event

// Track processed message IDs to prevent duplicates
const processedMessageIds = new Set();
const MAX_PROCESSED_IDS = 100; // Keep only last 100 message IDs

/**
 * Initialize the BroadcastChannel
 * @returns {BroadcastChannel|null} Channel instance or null if not supported
 */
const initChannel = () => {
  if (authChannel) return authChannel;
  
  // Check browser support
  if (typeof BroadcastChannel === 'undefined') {
    console.warn('🔄 AuthChannel: BroadcastChannel not supported in this browser');
    return null;
  }
  
  try {
    authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
    
    // Setup message listener
    authChannel.onmessage = (event) => {
      const { type, payload, timestamp, tabId: sourceTabId, messageId } = event.data || {};
      
      // Ignore messages from same tab
      if (sourceTabId === getTabId()) return;
      
      // Check for duplicate messages using messageId
      if (messageId && processedMessageIds.has(messageId)) {
        return;
      }
      
      // Add messageId to processed set
      if (messageId) {
        processedMessageIds.add(messageId);
        // Clean up old IDs if set gets too large
        if (processedMessageIds.size > MAX_PROCESSED_IDS) {
          const idsArray = Array.from(processedMessageIds);
          processedMessageIds.clear();
          idsArray.slice(-50).forEach(id => processedMessageIds.add(id));
        }
      }
      
      // CRITICAL: Aggressive throttle for logout events to prevent infinite loops
      if (type === AUTH_EVENTS.LOGOUT) {
        const now = Date.now();
        
        // Global cooldown - ignore ANY logout within cooldown period
        if ((now - lastLogoutProcessedTime) < GLOBAL_LOGOUT_COOLDOWN_MS) {
          return;
        }
        
        // If we received a logout from the same tab within throttle period, ignore it
        if (sourceTabId === lastLogoutSourceTabId && (now - lastLogoutProcessedTime) < LOGOUT_THROTTLE_MS) {
          return;
        }
        
        lastLogoutProcessedTime = now;
        lastLogoutSourceTabId = sourceTabId;
      }
      
      // Notify all registered handlers
      messageHandlers.forEach(handler => {
        try {
          handler({ type, payload, timestamp, tabId: sourceTabId });
        } catch (error) {
          console.error('🔄 AuthChannel: Handler error:', error);
        }
      });
    };
    
    return authChannel;
  } catch (error) {
    console.error('🔄 AuthChannel: Failed to initialize:', error);
    return null;
  }
};

/**
 * Get unique tab identifier
 * @returns {string} Unique tab ID
 */
let tabId = null;
const getTabId = () => {
  if (!tabId) {
    tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  return tabId;
};

/**
 * Broadcast a message to all other tabs
 * @param {string} type - Message type from AUTH_EVENTS
 * @param {any} payload - Data to send
 */
// Generate unique message ID
const generateMessageId = () => `${getTabId()}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Track last broadcast time for each event type to prevent self-spam
const lastBroadcastTime = {};
const BROADCAST_COOLDOWN_MS = 2000; // Minimum time between same event broadcasts

const broadcast = (type, payload = null) => {
  const channel = initChannel();
  if (!channel) return;
  
  // Prevent rapid-fire broadcasts of the same event type
  const now = Date.now();
  if (lastBroadcastTime[type] && (now - lastBroadcastTime[type]) < BROADCAST_COOLDOWN_MS) {
    return;
  }
  lastBroadcastTime[type] = now;
  
  const message = {
    type,
    payload,
    timestamp: now,
    tabId: getTabId(),
    messageId: generateMessageId(),
  };
  
  try {
    channel.postMessage(message);
  } catch (error) {
    console.error('🔄 AuthChannel: Broadcast failed:', error);
  }
};

/**
 * Register a message handler
 * @param {Function} handler - Callback function (receives { type, payload, timestamp, tabId })
 * @returns {Function} Unsubscribe function
 */
const subscribe = (handler) => {
  if (typeof handler !== 'function') {
    console.warn('🔄 AuthChannel: Invalid handler provided');
    return () => {};
  }
  
  // Initialize channel if not already
  initChannel();
  
  messageHandlers.push(handler);
  
  // Return unsubscribe function
  return () => {
    messageHandlers = messageHandlers.filter(h => h !== handler);
  };
};

/**
 * Broadcast login event with session data
 * @param {Object} sessionData - { accessToken, refreshToken, refreshTokenExpiryTime, user }
 */
const broadcastLogin = (sessionData) => {
  broadcast(AUTH_EVENTS.LOGIN, sessionData);
};

/**
 * Broadcast logout event
 */
const broadcastLogout = () => {
  broadcast(AUTH_EVENTS.LOGOUT);
};

/**
 * Broadcast token update (e.g., after refresh)
 * @param {Object} tokenData - { accessToken, refreshToken, refreshTokenExpiryTime, user }
 */
const broadcastTokenUpdate = (tokenData) => {
  broadcast(AUTH_EVENTS.TOKEN_UPDATE, tokenData);
};

/**
 * Request session from other tabs (for new tab initialization)
 */
const requestSession = () => {
  broadcast(AUTH_EVENTS.SESSION_REQUEST);
};

/**
 * Respond to session request with current session data
 * @param {Object} sessionData - Current session data
 */
const respondWithSession = (sessionData) => {
  broadcast(AUTH_EVENTS.SESSION_RESPONSE, sessionData);
};

/**
 * Close the channel (cleanup)
 */
const closeChannel = () => {
  if (authChannel) {
    authChannel.close();
    authChannel = null;
    messageHandlers = [];
  }
};

// Auth Channel Service API
const AuthChannelService = {
  // Core methods
  init: initChannel,
  broadcast,
  subscribe,
  close: closeChannel,
  
  // Convenience methods
  broadcastLogin,
  broadcastLogout,
  broadcastTokenUpdate,
  requestSession,
  respondWithSession,
  
  // Constants
  AUTH_EVENTS,
  
  // Utilities
  getTabId,
};

export default AuthChannelService;
